import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  doc,
  serverTimestamp,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Create a notification for admin or a specific student.
 * `link` should be an in-app path — clicking the bell navigates there.
 */
export async function createNotification({
  type = 'info',
  title,
  message,
  targetRole = 'admin',
  targetUserId = '',
  link = '',
  meta = {},
}) {
  if (!title) return null;
  return addDoc(collection(db, 'notifications'), {
    type,
    title,
    message: message || '',
    targetRole,
    targetUserId: targetUserId || '',
    link: link || '',
    meta: meta || {},
    read: false,
    createdAt: serverTimestamp(),
  });
}

export async function notifyAdmin({ type = 'info', title, message, link = '', meta = {} }) {
  return createNotification({ type, title, message, targetRole: 'admin', link, meta });
}

export async function notifyStudent({ userId, type = 'info', title, message, link = '', meta = {} }) {
  if (!userId) return null;
  return createNotification({
    type,
    title,
    message,
    targetRole: 'student',
    targetUserId: userId,
    link,
    meta,
  });
}

export async function notifyAdminNewInquiry(inquiry) {
  return notifyAdmin({
    type: 'inquiry',
    title: 'New registration inquiry',
    message: `${inquiry.fullName} submitted a contact form (${inquiry.email})`,
    link: '/admin?tab=inquiries',
    meta: { inquiryId: inquiry.id || '', email: inquiry.email },
  });
}

export async function notifyStudentEnrolled({ userId, registrationNumber, planLabel }) {
  return notifyStudent({
    userId,
    type: 'enrollment',
    title: 'Welcome to Success Bridge Academy',
    message: `Your account is active${registrationNumber ? ` · Reg No ${registrationNumber}` : ''}${planLabel ? ` · ${planLabel}` : ''}`,
    link: '/dashboard',
    meta: { registrationNumber },
  });
}

export async function notifyAdminStudentEnrolled({ studentName, email, registrationNumber, unpaid = false }) {
  return notifyAdmin({
    type: 'enrollment',
    title: unpaid ? 'Student enrolled — payment pending' : 'New student enrolled',
    message: unpaid
      ? `${studentName || email} created an account and has not paid yet`
      : `${studentName || email} enrolled${registrationNumber ? ` · Reg ${registrationNumber}` : ''}`,
    link: unpaid ? '/admin?tab=unpaid-enrollments' : '/admin?tab=subscriptions',
    meta: { email, registrationNumber, unpaid: Boolean(unpaid) },
  });
}

export async function notifyAdminStudentLogin({ displayName, email, userId }) {
  return notifyAdmin({
    type: 'login',
    title: 'Student signed in',
    message: `${displayName || email} logged into the portal`,
    link: '/admin?tab=subscriptions',
    meta: { email, userId },
  });
}

export async function notifyAdminPayment({ amount, referenceNumber, method }) {
  const methodBit = method ? ` · ${method}` : '';
  return notifyAdmin({
    type: 'payment',
    title: method && /cash/i.test(method) ? 'Cash payment received' : 'Bank checkout payment received',
    message: `Payment received${amount != null ? ` · $${amount}` : ''}${referenceNumber ? ` · Ref ${referenceNumber}` : ''}${methodBit}`,
    link: '/admin?tab=payments',
    meta: { referenceNumber, method },
  });
}

export async function notifyStudentPayment({ userId, amount, planLabel, method, referenceNumber }) {
  const methodBit = method ? ` via ${method}` : '';
  return notifyStudent({
    userId,
    type: 'payment',
    title: 'Payment confirmed',
    message: `Your payment${amount != null ? ` of $${amount}` : ''}${methodBit} was received${planLabel ? ` · ${planLabel}` : ''}${referenceNumber ? ` · ${referenceNumber}` : ''}`,
    link: '/payments',
    meta: { amount, planLabel, method },
  });
}

export async function notifyAdminNewMessage({ studentName, studentUserId, preview }) {
  return notifyAdmin({
    type: 'message',
    title: 'New student message',
    message: `${studentName || 'A student'}: ${(preview || '').slice(0, 80)}`,
    link: `/admin?tab=messages&student=${studentUserId}`,
    meta: { studentUserId },
  });
}

export async function notifyStudentNewMessage({ userId, preview }) {
  return notifyStudent({
    userId,
    type: 'message',
    title: 'Reply from Success Bridge Academy',
    message: (preview || 'You have a new message').slice(0, 120),
    link: '/messages',
    meta: {},
  });
}

export async function notifyAdminExamCompleted({ studentName, email, subject, score }) {
  return notifyAdmin({
    type: 'exam',
    title: 'Practice exam completed',
    message: `${studentName || email} finished ${subject || 'an exam'}${score != null ? ` · Score ${score}%` : ''}`,
    link: '/admin?tab=student-progress',
    meta: { email, subject, score },
  });
}

export async function notifyStudentExamCompleted({ userId, subject, score }) {
  return notifyStudent({
    userId,
    type: 'exam',
    title: 'Exam results ready',
    message: `Your ${subject || 'practice exam'} is complete${score != null ? ` · Score ${score}%` : ''}`,
    link: '/exams',
    meta: { subject, score },
  });
}

export async function notifyAdminSecurityBlock({ studentName, email, userId, reason }) {
  return notifyAdmin({
    type: 'security',
    title: 'Student portal blocked',
    message: `${studentName || email} blocked: ${reason}`,
    link: `/admin?tab=subscriptions&student=${userId}`,
    meta: { userId, email, reason },
  });
}

export async function notifyAdminDeviceApproval({ studentName, email, userId, deviceLabel }) {
  return notifyAdmin({
    type: 'device',
    title: 'Device approval needed',
    message: `${studentName || email} wants to sign in from ${deviceLabel || 'a new device'}`,
    link: `/admin?tab=subscriptions&student=${userId}&approveDevice=1`,
    meta: { userId, email },
  });
}

export async function notifyStudentDevicePending({ userId }) {
  return notifyStudent({
    userId,
    type: 'device',
    title: 'Device pending approval',
    message: 'Your login from this device is waiting for admin approval.',
    link: '/dashboard',
    meta: {},
  });
}

export async function notifyStudentDeviceApproved({ userId }) {
  return notifyStudent({
    userId,
    type: 'device',
    title: 'Device approved',
    message: 'You can now use this device to access your portal.',
    link: '/dashboard',
    meta: {},
  });
}

export function subscribeAdminNotifications(callback) {
  const q = query(
    collection(db, 'notifications'),
    where('targetRole', '==', 'admin'),
    orderBy('createdAt', 'desc'),
    limit(50)
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

export function subscribeStudentNotifications(userId, callback) {
  if (!userId) return () => {};
  const q = query(
    collection(db, 'notifications'),
    where('targetUserId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(40)
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

export async function markNotificationRead(id) {
  await updateDoc(doc(db, 'notifications', id), { read: true });
}

export async function markAllNotificationsRead(notifications) {
  const unread = (notifications || []).filter((n) => !n.read);
  await Promise.all(unread.map((n) => markNotificationRead(n.id)));
}
